<template>
  <div class="salon-profile">
    <button
      ref="trigger"
      class="salon-profile-btn"
      type="button"
      aria-haspopup="menu"
      :aria-expanded="String(isOpen)"
      :aria-label="triggerLabel"
      @click="toggle"
    >
      <span v-if="initials" class="salon-profile-initials">{{ initials }}</span>
      <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 4.5V20h14v-1.5c0-2.5-3-4.5-7-4.5z"
          fill="currentColor" />
      </svg>
    </button>

    <!-- The sheet is anchored to the avatar rather than centred, so the customer keeps
         sight of the screen they were on: this menu is a detour, not a new place. -->
    <div v-if="isOpen" class="salon-profile-sheet" role="menu" :aria-label="triggerLabel">
      <p v-if="name" class="salon-profile-who">{{ name }}</p>

      <button
        v-for="entry in entries"
        :key="entry.key"
        class="salon-profile-item"
        type="button"
        role="menuitem"
        @click="go(entry.key)"
      >
        <span class="salon-profile-item-icon" aria-hidden="true" v-html="entry.icon"></span>
        <span class="salon-profile-item-label">{{ $t(entry.labelKey) }}</span>
      </button>

      <!-- SH046 — a setting rather than a destination: it switches in place and leaves the
           sheet open, so the customer watches the labels above change and can switch back. -->
      <div class="salon-profile-lang" role="group" :aria-label="$t('salon.menu.language')">
        <span class="salon-profile-item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20"><circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M3.5 12h17M12 3.5c2.3 2.4 3.5 5.2 3.5 8.5s-1.2 6.1-3.5 8.5c-2.3-2.4-3.5-5.2-3.5-8.5s1.2-6.1 3.5-8.5z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>
        </span>
        <span class="salon-profile-item-label">{{ $t("salon.menu.language") }}</span>
        <div class="salon-lang-switch">
          <button
            v-for="option in languages"
            :key="option.code"
            class="salon-lang-option"
            :class="{ 'is-active': option.code === $i18n.locale }"
            type="button"
            :lang="option.code"
            :aria-pressed="String(option.code === $i18n.locale)"
            @click="chooseLanguage(option.code)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <!-- SH022 — Log out is not a fourth destination, it ends the session, so it sits
           below a rule rather than in the list the entries are drawn from. -->
      <div class="salon-profile-rule" role="separator"></div>

      <button
        class="salon-profile-item salon-profile-item--danger"
        type="button"
        role="menuitem"
        @click="askLogout"
      >
        <span class="salon-profile-item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20"><path d="M14 4H6a2 2 0 00-2 2v12a2 2 0 002 2h8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M17 8l4 4-4 4M21 12H10" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
        <span class="salon-profile-item-label">{{ $t("salon.menu.logOut") }}</span>
      </button>
    </div>

    <!-- Logging out throws away a half-made booking, so it asks first. Teleported because
         the avatar sits in a `transform`ed box, and a transform makes its descendants'
         `position: fixed` resolve against that 36px button instead of the viewport.
         It carries `salon-app` back with it: tokens.css hangs the whole salon palette off
         that class, so outside it every var(--salon-*) here resolves to nothing. -->
    <Teleport to="body">
      <div v-if="isConfirmingLogout" class="salon-app salon-confirm">
        <div class="salon-confirm-scrim" @click="dismissLogout"></div>
        <div class="salon-confirm-card" role="dialog" aria-modal="true" :aria-label="$t('salon.menu.logOut')">
          <h3 class="salon-confirm-title">{{ $t("salon.menu.logOutTitle") }}</h3>
          <p class="salon-confirm-text">
            {{ $t("salon.menu.logOutText") }}
          </p>
          <button class="salon-confirm-danger" type="button" @click="confirmLogout">
            {{ $t("salon.menu.logOut") }}
          </button>
          <button class="salon-confirm-keep" type="button" @click="dismissLogout">
            {{ $t("salon.common.cancel") }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { setLanguagePair, setLocale } from "@/i18n";
import "@/salon/styles/tokens.css";

/**
 * SH016 — the profile menu behind the avatar in the salon top bar.
 *
 * Every salon screen is a step of BookingFlow rather than a route, so this emits the
 * destination and lets that container move the flow; it never touches the router itself.
 *
 * The entries are the only places a signed-in customer can jump to sideways: their own
 * record, SH044's stylist vote, back to the service picker, their vouchers, or their
 * appointments. SH022 adds Log out under them, which emits the same way once the customer
 * has confirmed it.
 */
const ENTRIES = [
  // SH024 — first, because it is who the avatar above it stands for rather than somewhere
  // else to go.
  {
    key: "profile",
    labelKey: "salon.menu.profile",
    icon:
      '<svg viewBox="0 0 24 24" width="20" height="20"><circle cx="12" cy="8.5" r="3.5" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M5 20v-.5c0-2.8 3.1-4.5 7-4.5s7 1.7 7 4.5V20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  },
  // SH044 — directly under Profile, where the task put it: it is the other thing the
  // avatar leads to that is about the customer's own opinion rather than their booking.
  {
    key: "vote",
    labelKey: "salon.menu.vote",
    icon:
      '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8L12 4z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
  },
  {
    key: "order",
    labelKey: "salon.menu.order",
    icon:
      '<svg viewBox="0 0 24 24" width="20" height="20"><circle cx="6" cy="6" r="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="6" cy="18" r="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8 7.5L20 18M8 16.5L20 6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  },
  {
    key: "awards",
    labelKey: "salon.menu.awards",
    icon:
      '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M4 9h16v3a2 2 0 000 4v3H4v-3a2 2 0 000-4V9z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M12 9v10" fill="none" stroke="currentColor" stroke-width="1.7" stroke-dasharray="2 2.5"/><path d="M12 9c-2.5 0-4-1-4-2.5S9.5 4 12 6c2.5-2 4-1 4 .5S14.5 9 12 9z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
  },
  {
    key: "bookings",
    labelKey: "salon.menu.bookings",
    icon:
      '<svg viewBox="0 0 24 24" width="20" height="20"><rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M3 10h18M8 3v4M16 3v4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M8.5 15l2.2 2.2L15.5 12.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  },
];

// SH046 — salon offers English and Chinese only. Each option is written in its own
// language, so a customer who cannot read the current one can still find theirs.
const LANGUAGES = [
  { code: "en", label: "ENG" },
  { code: "zh", label: "中文" },
];

export default {
  name: "SalonProfileMenu",
  props: {
    // Drawn in the avatar. Falls back to a person glyph — a returning customer who
    // registered without a name would otherwise get an empty circle.
    initials: {
      type: String,
      default: "",
    },
    // Named above the entries so the customer can see whose account this is before
    // walking off into their record, their vouchers or their appointments.
    name: {
      type: String,
      default: "",
    },
  },
  emits: ["navigate"],
  data() {
    return {
      isOpen: false,
      isConfirmingLogout: false,
      entries: ENTRIES,
      languages: LANGUAGES,
    };
  },
  computed: {
    triggerLabel() {
      return this.name
        ? this.$t("salon.menu.accountMenuFor", { name: this.name })
        : this.$t("salon.menu.accountMenu");
    },
  },
  // The listeners only exist while the sheet is up, but a screen can be swapped out from
  // under an open menu (BookingFlow changes step), so they are torn down here too.
  beforeUnmount() {
    this.unbind();
  },
  methods: {
    toggle() {
      if (this.isOpen) {
        this.close();
        return;
      }

      this.isOpen = true;
      document.addEventListener("click", this.onDocumentClick, true);
      document.addEventListener("keydown", this.onKeydown);
    },

    close() {
      this.isOpen = false;
      this.unbind();
    },

    unbind() {
      document.removeEventListener("click", this.onDocumentClick, true);
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("keydown", this.onConfirmKeydown);
    },

    // Capture phase, so a tap anywhere else closes the menu before that element acts on
    // it. Clicks inside are left alone — the entries close the menu themselves.
    onDocumentClick(event) {
      if (this.$el && this.$el.contains(event.target)) {
        return;
      }

      this.close();
    },

    onKeydown(event) {
      if (event.key === "Escape" || event.key === "Esc") {
        this.close();
        this.$refs.trigger?.focus();
      }
    },

    go(key) {
      this.close();
      this.$emit("navigate", key);
    },

    // SH046 — the pair is pinned to Chinese as well as the locale set: restoreLocale()
    // clamps a reload to {en, pair}, so a pair left on another language by MOO's top bar
    // would quietly bring a Chinese choice back up in English.
    chooseLanguage(code) {
      setLanguagePair("zh");
      setLocale(code);
    },

    // SH022 — the sheet gets out of the way while the customer decides; the confirmation
    // covers the screen on its own, and the entries behind it would only be in the way.
    askLogout() {
      this.close();
      this.isConfirmingLogout = true;
      document.addEventListener("keydown", this.onConfirmKeydown);
    },

    dismissLogout() {
      this.isConfirmingLogout = false;
      document.removeEventListener("keydown", this.onConfirmKeydown);
      this.$refs.trigger?.focus();
    },

    // Same channel as the entries: the container owns the session and the step, not this.
    confirmLogout() {
      this.dismissLogout();
      this.$emit("navigate", "logout");
    },

    onConfirmKeydown(event) {
      if (event.key === "Escape" || event.key === "Esc") {
        this.dismissLogout();
      }
    },
  },
};
</script>

<style scoped>
/* Anchored to `.salon-topbar-row`, which every salon screen draws as a centred, relative
   row — the same hook the back arrow hangs off on the other side. */
.salon-profile {
  position: absolute;
  right: -4px;
  top: 50%;
  transform: translateY(-50%);
}

.salon-profile-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  font-family: var(--salon-font-headline);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--salon-primary);
  background: #f0ebe3;
  border: 2px solid var(--salon-secondary);
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(28, 28, 30, 0.06);
  cursor: pointer;
}

.salon-profile-btn:active {
  transform: scale(0.95);
}

.salon-profile-initials {
  line-height: 1;
}

.salon-profile-sheet {
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  z-index: 60;
  min-width: 184px;
  padding: 6px;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: var(--salon-radius-card);
  box-shadow: 0 12px 32px rgba(28, 28, 30, 0.14);
}

.salon-profile-who {
  margin: 0;
  padding: 8px 12px 10px;
  font-family: var(--salon-font-body);
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
  border-bottom: 1px solid var(--salon-border);
}

.salon-profile-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  font-family: var(--salon-font-body);
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: var(--salon-on-surface);
  background: transparent;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
}

.salon-profile-item + .salon-profile-item {
  margin-top: 2px;
}

.salon-profile-item:hover,
.salon-profile-item:focus-visible {
  background: var(--salon-surface-container-low);
}

.salon-profile-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Language (SH046) ──────────────────────────────────────────── */
.salon-profile-lang {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 2px;
  padding: 8px 8px 8px 12px;
  font-family: var(--salon-font-body);
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-profile-lang .salon-profile-item-label {
  flex: 1;
  white-space: nowrap;
}

.salon-lang-switch {
  display: flex;
  padding: 2px;
  background: var(--salon-surface-container-low);
  border: 1px solid var(--salon-border);
  border-radius: 999px;
}

.salon-lang-option {
  min-width: 44px;
  padding: 4px 10px;
  font-family: var(--salon-font-body);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.salon-lang-option.is-active {
  color: var(--salon-on-primary);
  background: var(--salon-primary);
}

/* ── Log out ───────────────────────────────────────────────────── */
.salon-profile-rule {
  height: 1px;
  margin: 6px 4px;
  background: var(--salon-border);
}

.salon-profile-item--danger,
.salon-profile-item--danger .salon-profile-item-icon {
  color: var(--salon-error);
}

/* ── Log out confirmation ──────────────────────────────────────── */
.salon-confirm {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.salon-confirm-scrim {
  position: absolute;
  inset: 0;
  background: rgba(28, 28, 30, 0.45);
}

.salon-confirm-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 24px var(--salon-container-padding)
    calc(24px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface-lowest);
  border-radius: 20px 20px 0 0;
}

.salon-confirm-title {
  margin: 0 0 8px;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-confirm-text {
  margin: 0 0 24px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-confirm-danger {
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: #ffffff;
  background: var(--salon-error);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.salon-confirm-keep {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 0;
  cursor: pointer;
}
</style>
